import { ReferencedEntry } from 'contentful';
import noop from 'lodash/noop';
import { useCallback, useState } from 'react';

import { HasImage, HasVideo, Navigation } from 'src/api/contentful/contentTypes.cloverdotcom';

export type SidebarContext = Readonly<{
  override?: ReferencedEntry<Navigation>;
  setOverride: (entry?: ReferencedEntry<Navigation>) => void;
  clearOverride: () => void;
  backgroundColor?: string;
  media?: ReferencedEntry<HasVideo | HasImage>;
  isOpen: boolean;
  ignoreWindowEvents: boolean;
  setMedia: (media?: ReferencedEntry<HasVideo | HasImage>) => void;
  setBackgroundColor: (backgroundColor?: string) => void;
  open: () => void;
  close: () => void;
  disableWindowEvents: () => void;
  enableWindowEvents: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isOpenable: boolean;
  setOpenability: (isOpenable: boolean) => void;
}>;

export const initialValue: SidebarContext = {
  override: undefined,
  setOverride: noop,
  clearOverride: noop,
  isOpen: false,
  ignoreWindowEvents: false,
  setMedia: noop,
  setBackgroundColor: noop,
  open: noop,
  close: noop,
  disableWindowEvents: noop,
  enableWindowEvents: noop,
  isLoading: false,
  setIsLoading: noop,
  isOpenable: false,
  setOpenability: noop,
};

export const useSidebarInternal = (): SidebarContext => {
  const [ override, setOverride ] = useState<ReferencedEntry<Navigation> | undefined>();
  const [ media, setMedia ] = useState<ReferencedEntry<HasVideo | HasImage> | undefined>();
  const [ backgroundColor, setBackgroundColor ] = useState<string | undefined>();
  const [ isOpen, setIsOpen ] = useState(initialValue.isOpen);
  const [ ignoreWindowEvents, setIgnoreWindowEvents ] = useState(initialValue.ignoreWindowEvents);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isOpenable, setIsOpenable ] = useState(true);

  const clearOverride = useCallback(() => setOverride(undefined), [ setOverride ]);
  const open = useCallback(() => setIsOpen(isOpenable), [ setIsOpen, isOpenable ]);
  const close = useCallback(() => setIsOpen(false), [ setIsOpen ]);
  const setOpenability = useCallback((isOpenable: boolean) => {
    setIsOpenable(isOpenable);
    if (!isOpenable && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, setIsOpen]);
  const disableWindowEvents = useCallback(() => setIgnoreWindowEvents(true), [ setIgnoreWindowEvents ]);
  const enableWindowEvents = useCallback(() => setIgnoreWindowEvents(false), [ setIgnoreWindowEvents ]);

  return {
    override,
    setOverride,
    clearOverride,
    backgroundColor,
    media,
    isOpen,
    ignoreWindowEvents,
    setMedia,
    setBackgroundColor,
    open,
    close,
    disableWindowEvents,
    enableWindowEvents,
    isLoading,
    setIsLoading,
    isOpenable,
    setOpenability,
  };
};
